import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { Badge, Button, SelectField, TextField, type ListColumn } from "@wilco/ui";
import { FormPage, useDocumentForm } from "../docs/DocumentForm.tsx";
import { clientPaged, DocumentList } from "../docs/DocumentList.tsx";
import { formatDate, formatNumber, t } from "../i18n.ts";
import { warehouses } from "../lookups.ts";
import { salesApi } from "../sales/api.ts";
import { ProductSearch } from "../stock/RequestDraftPage.tsx";
import { parseQty } from "../stock/qty.ts";
import { productionApi } from "./api.ts";
import { ORDER_STATE } from "./MoPage.tsx";
import { PRODUCTION } from "./queries.ts";
import type { ManufacturingOrder, OrderState } from "./types.ts";

const route = getRouteApi("/app/production/orders");
const STATES = Object.keys(ORDER_STATE) as OrderState[];

/** Склады деятельности, где собирают: серия, R&D и БПАК. */
const MAKERS = ["SER", "RND", "BPK"];

const load = clientPaged(productionApi.orders, {
  text: (o) => `${o.number} ${o.productName} ${o.salesOrder ?? ""}`,
  status: (o) => o.state,
  compare: {
    start: (a, b) => a.start.localeCompare(b.start),
    startDesc: (a, b) => b.start.localeCompare(a.start),
    number: (a, b) => b.number.localeCompare(a.number, "uk", { numeric: true }),
  },
});

/** Заказы на производство на общем списке документов: что, сколько, где, когда, под что. */
export function ProductionOrdersPage() {
  const search = route.useSearch();
  const navigate = useNavigate();
  const columns: ListColumn<ManufacturingOrder>[] = [
    { key: "number", header: t("mo.list.number"), kind: "title", width: "9rem", render: (o) => <span className="w-num">{o.number}</span> },
    { key: "product", header: t("mo.product"), kind: "text", render: (o) => `${o.productName}${o.version ? ` ${o.version}` : ""}` },
    { key: "warehouse", header: t("mo.warehouse"), kind: "text", width: "9rem", render: (o) => o.warehouseName },
    { key: "state", header: t("mo.list.state"), kind: "text", width: "9rem", render: (o) => <Badge tone={ORDER_STATE[o.state].tone}>{t(ORDER_STATE[o.state].key)}</Badge> },
    { key: "qty", header: t("mo.list.qty"), kind: "number", width: "5rem", render: (o) => formatNumber(o.qty) },
    { key: "start", header: t("mo.start"), kind: "number", width: "7rem", render: (o) => formatDate(o.start) },
  ];
  return (
    <DocumentList
      title={t("mo.list.title")}
      icon="mrp"
      queryKey={[...PRODUCTION, "orders", "list"]}
      load={load}
      columns={columns}
      rowKey={(o) => o.id}
      onOpen={(o) => void navigate({ to: "/production/orders/$id", params: { id: o.id } })}
      statuses={STATES.map((s) => ({ value: s, label: t(ORDER_STATE[s].key) }))}
      sorts={[
        { value: "startDesc", label: t("mo.sort.startDesc") },
        { value: "start", label: t("mo.sort.start") },
        { value: "number", label: t("mo.sort.number") },
      ]}
      search={search}
      onSearch={(next) => void navigate({ to: "/production/orders", search: next, replace: true })}
      searchHint={t("mo.list.searchHint")}
      create={{ label: t("mo.new"), to: "/production/orders/new", permission: "manufacturing.order" }}
      empty={{ title: t("mo.list.empty"), body: t("mo.list.emptyBody") }}
    />
  );
}

interface NewOrder {
  productId: string;
  productLabel: string;
  version: string;
  qty: string;
  warehouseId: string;
  start: string;
  /** "" на склад, иначе id заказа покупателя. */
  salesOrderId: string;
}

const today = () => new Date().toISOString().slice(0, 10);

/**
 * «Нове замовлення» на производство: изделие и версия из спецификации,
 * количество, склад деятельности (серия, R&D, БПАК), на склад или под
 * заказ покупателя (ПС-01). Сервер создаёт черновик, «Підтвердити» в
 * карточке даёт код партии и заявки складу.
 */
export function NewProductionOrderPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const places = useQuery({ queryKey: ["lookups", "warehouses"], queryFn: warehouses, select: (l) => l.filter((w) => MAKERS.includes(w.code)) });
  const sales = useQuery({ queryKey: ["sales", "orders", "confirmed"], queryFn: salesApi.orders, select: (l) => l.filter((o) => o.state === "CONFIRMED") });
  const form = useDocumentForm<NewOrder, ManufacturingOrder>({
    initial: { productId: "", productLabel: "", version: "", qty: "1", warehouseId: "", start: today(), salesOrderId: "" },
    validate: (v) => ({
      productId: v.productId ? undefined : t("docs.required"),
      qty: (parseQty(v.qty) ?? 0) > 0 && Number.isInteger(parseQty(v.qty)) ? undefined : t("mo.new.qtyInvalid"),
      warehouseId: v.warehouseId ? undefined : t("docs.required"),
      start: v.start ? undefined : t("docs.required"),
    }),
    submit: (v, key) =>
      productionApi.create(
        {
          productId: v.productId,
          version: v.version || null,
          qty: parseQty(v.qty) ?? 0,
          warehouseId: v.warehouseId,
          start: v.start,
          salesOrderType: v.salesOrderId ? "so" : null,
          salesOrderId: v.salesOrderId || null,
        },
        key,
      ),
    onSuccess: (o) => {
      void queryClient.invalidateQueries({ queryKey: PRODUCTION });
      void navigate({ to: "/production/orders/$id", params: { id: o.id }, replace: true });
    },
    fieldOf: (code) => (code === "manufacturing.no_bom" ? "productId" : code.includes("version") ? "version" : undefined),
  });
  const { values, set, errors } = form;
  const bom = useQuery({ queryKey: [...PRODUCTION, "bom", values.productId], queryFn: () => productionApi.bom(values.productId), enabled: Boolean(values.productId) });
  const versions = bom.data?.versions.filter((v) => !v.archived) ?? [];

  return (
    <FormPage title={t("mo.new")} back="/production/orders" submitLabel={t("mo.new.create")} form={form}>
      {values.productId ? (
        <div className="w-field">
          <span className="w-field-label">{t("mo.product")}</span>
          <p className="sales-picked">
            {values.productLabel}{" "}
            <Button
              variant="ghost"
              type="button"
              onClick={() => {
                set("productId", "");
                set("version", "");
              }}
            >
              {t("sales.change")}
            </Button>
          </p>
          {errors.productId ? (
            <span className="w-field-error" role="alert">
              {errors.productId}
            </span>
          ) : null}
          {bom.data === null ? <span className="w-field-hint">{t("mo.new.noBom")}</span> : null}
        </div>
      ) : (
        <div className="w-field">
          <ProductSearch
            onPick={(p) => {
              set("productId", p.id);
              set("productLabel", `${p.sku} ${p.name}`);
            }}
          />
          {errors.productId ? (
            <span className="w-field-error" role="alert">
              {errors.productId}
            </span>
          ) : null}
        </div>
      )}
      {versions.length > 0 ? (
        <SelectField
          label={t("mo.new.version")}
          value={values.version}
          onChange={(e) => set("version", e.target.value)}
          options={[{ value: "", label: t("mo.new.versionHeader") }, ...versions.map((v) => ({ value: v.code, label: v.code }))]}
          error={errors.version}
        />
      ) : null}
      <TextField label={t("mo.list.qty")} inputMode="numeric" value={values.qty} onChange={(e) => set("qty", e.target.value)} error={errors.qty} />
      <SelectField
        label={t("mo.warehouse")}
        value={values.warehouseId}
        onChange={(e) => set("warehouseId", e.target.value)}
        options={[{ value: "", label: t("docs.choose") }, ...(places.data ?? []).map((w) => ({ value: w.id, label: w.name }))]}
        error={errors.warehouseId}
      />
      <TextField label={t("mo.start")} type="date" value={values.start} onChange={(e) => set("start", e.target.value)} error={errors.start} />
      <SelectField
        label={t("mo.new.target")}
        hint={t("mo.new.targetHint")}
        value={values.salesOrderId}
        onChange={(e) => set("salesOrderId", e.target.value)}
        options={[{ value: "", label: t("mo.new.toStock") }, ...(sales.data ?? []).map((o) => ({ value: o.id, label: `${o.number} · ${o.customerName}` }))]}
        error={errors.salesOrderId}
      />
    </FormPage>
  );
}
